import React, { useState, useEffect } from 'react';
import {
    Box, VStack, HStack, Text, Card, CardBody, Badge,
    Button, Tabs, TabList, Tab, TabPanels, TabPanel,
    Image, Icon, Divider, Spinner, useToast, Textarea,
    ModalCloseButton, ModalBody, Collapse, Flex, Modal, ModalOverlay, ModalContent
} from '@chakra-ui/react';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { FiCalendar, FiCheckCircle, FiEdit3, FiSmile, FiActivity, FiPlus, FiMinus } from 'react-icons/fi';
import { getTodayDateString, formatTime } from '../utils/dateUtils';
import { authAPI, resolveImageUrl } from '../services/api';
import { fetchAllPages } from '../services/fetchAll';
import LoadingOverlay from '../components/LoadingOverlay';
import LogoPulseLoader from '../components/LogoPulseLoader';

const getDisplayLabel = (label) => {
    const map = {
        '小一': '一年級',
        '小二': '二年級',
        '小三': '三年級',
        '小四': '四年級',
        '小五': '五年級',
        '小六': '六年級',
        '七年級': '七年級 (國一)',
        '八年級': '八年級 (國二)',
        '九年級': '九年級 (國三)',
        '全班': '全班事項',
        '英文': '英文作業'
    };
    return map[label] || label;
};

const getMoodLabel = (mood) => {
    const map = {
        'HAPPY': '開心',
        'NORMAL': '平靜',
        'SAD': '難過',
        'EXCITED': '興奮',
        'TIRED': '疲累'
    };
    return map[mood] || mood;
};

const getHealthLabel = (health) => {
    const map = {
        'GOOD': '良好',
        'SICK': '生病',
        'INJURED': '受傷'
    };
    return map[health] || health;
};

const ParentCommBookPage = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [children, setChildren] = useState([]);
    const [selectedChildIndex, setSelectedChildIndex] = useState(0);

    // Data
    const [books, setBooks] = useState([]);
    const [expandedBookId, setExpandedBookId] = useState(null);

    const [signComment, setSignComment] = useState('');
    const [isSigning, setIsSigning] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [visibleCount, setVisibleCount] = useState(7);
    const [selectedClassId, setSelectedClassId] = useState('all');

    // Fetch Children
    useEffect(() => {
        const fetchMyChildren = async () => {
            try {
                const res = await authAPI.getMe();
                setChildren(res.data.children || []);
            } catch (error) {
                console.error(error);
                toast({ title: "無法載入子女資料", status: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchMyChildren();

        // Refresh dashboard when leaving this page (to ensure badge count is up-to-date)
        return () => {
            window.dispatchEvent(new Event('data-refresh'));
        };
    }, []);

    // Fetch Books when child selected
    useEffect(() => {
        if (children.length > 0) {
            fetchBooks(children[selectedChildIndex].id);
        }
    }, [children, selectedChildIndex]);

    const fetchBooks = async (studentId) => {
        setLoading(true);
        setBooks([]);
        try {
            // [Hotfix 2026-08-01] 原本後端寫死 .limit(30)、前端一次抓完不分頁：232 位有
            // 聯絡簿的學生中 118 位（51%）超過 30 本，且學生普遍跨多課程，最活躍的課程
            // 會把 30 本額度佔滿，家長切到「才藝／英文」等其他課程分頁常常整個空白。
            // 改用 fetchAllPages 分頁抓完整份歷史，讓下面既有的班級篩選能篩到完整資料
            // （後端 skip/limit 見 app/routers/communication_book.py get_child_books）。
            const data = await fetchAllPages(
                ({ skip, limit }) => authAPI.communicationBook.getChildHistory(studentId, { skip, limit })
            );
            // Sort by date desc
            const sorted = data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setBooks(sorted);
            if (sorted.length > 0) {
                setExpandedBookId(sorted[0].id); // Auto expand latest
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSign = async (bookId, e) => {
        e.stopPropagation(); // Prevent card collapse
        const studentId = children[selectedChildIndex].id;
        setIsSigning(true);
        try {
            await authAPI.communicationBook.signBook(bookId, {
                book_id: bookId,
                student_id: studentId,
                comment: signComment
            });
            toast({ title: "簽名成功", status: "success" });
            fetchBooks(studentId);
            // Force refresh dashboard badge
            window.dispatchEvent(new Event('data-refresh'));
        } catch (error) {
            toast({ title: "簽名失敗", status: "error" });
        } finally {
            setIsSigning(false);
        }
    };

    const isSigned = (book) => {
        if (!book || !children[selectedChildIndex]) return false;
        return book.signatures && book.signatures.some(s => s.student_id === children[selectedChildIndex].id);
    };

    const toggleExpand = (id) => {
        if (expandedBookId === id) {
            setExpandedBookId(null);
        } else {
            setExpandedBookId(id);
            setSignComment('');
        }
    };

    const openLightbox = (url) => setSelectedImage(url);
    const closeLightbox = () => setSelectedImage(null);

    // Helper for class color coding
    const getClassColor = (name) => {
        if (!name) return 'gray';
        const colors = ['blue', 'green', 'purple', 'orange', 'pink', 'teal', 'cyan', 'red'];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    // Derived State for Pagination
    const filteredBooks = selectedClassId === 'all' ? books : books.filter(b => b.class_id === selectedClassId);
    const visibleBooks = filteredBooks.slice(0, visibleCount);
    const hasMore = filteredBooks.length > visibleCount;

    return (
        <>
            {loading && children.length === 0 ? (
                <Flex justify="center" align="center" h="60vh">
                    <LogoPulseLoader />
                </Flex>
            ) : (
                <VStack spacing={5} align="stretch" p={5} pb="150px">

                    {/* Child Tabs */}
                    {/* Child Tabs */}
                    {children.length > 0 && (
                        <VStack spacing={4} align="stretch">
                            <Tabs
                                variant="soft-rounded"
                                colorScheme="brand"
                                index={selectedChildIndex}
                                onChange={(index) => {
                                    setSelectedChildIndex(index);
                                    setSelectedClassId('all'); // Reset class filter when switching child
                                }}
                                size="lg"
                            >
                                <TabList
                                    overflowX="auto"
                                    py={2}
                                    px={1}
                                    sx={{
                                        gap: '12px',
                                        '::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none'
                                    }}
                                >
                                    {children.map((c, idx) => {
                                        const isSelected = selectedChildIndex === idx;
                                        return (
                                            <Tab
                                                key={c.id}
                                                flexShrink={0}
                                                borderRadius="full"
                                                py={3} px={6}
                                                bg={isSelected ? "brand.500" : "white"}
                                                color={isSelected ? "white" : "gray.600"}
                                                shadow={isSelected ? "md" : "sm"}
                                                _selected={{ color: "white", bg: "brand.500" }}
                                            >
                                                <Text fontWeight="bold">{c.full_name}</Text>
                                            </Tab>
                                        );
                                    })}
                                </TabList>
                            </Tabs>

                            {/* Class Sub-Tabs (Only if > 1 class) */}
                            {children[selectedChildIndex]?.classes?.length > 1 && (
                                <HStack
                                    spacing={3}
                                    overflowX="auto"
                                    w="100%"
                                    py={1} px={1}
                                    sx={{
                                        '::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none'
                                    }}
                                >
                                    <Button
                                        size="sm"
                                        borderRadius="full"
                                        variant={selectedClassId === 'all' ? 'solid' : 'outline'}
                                        colorScheme="brand"
                                        onClick={() => setSelectedClassId('all')}
                                        flexShrink={0}
                                    >
                                        全部
                                    </Button>
                                    {children[selectedChildIndex].classes.map(cls => (
                                        <Button
                                            key={cls.id}
                                            size="sm"
                                            borderRadius="full"
                                            variant={selectedClassId === cls.id ? 'solid' : 'outline'}
                                            colorScheme={getClassColor(cls.name)} // Use the mapped color for the button
                                            onClick={() => setSelectedClassId(cls.id)}
                                            leftIcon={<Badge bg="whiteAlpha.400" borderRadius="full" boxSize="8px" />}
                                            flexShrink={0}
                                        >
                                            {cls.name}
                                        </Button>
                                    ))}
                                </HStack>
                            )}
                        </VStack>
                    )}

                    {/* Books Feed */}
                    {loading ? (
                        <Flex justify="center" align="center" h="30vh">
                            <LogoPulseLoader />
                        </Flex>
                    ) : books.length === 0 ? (
                        <Flex direction="column" align="center" justify="center" py={20} color="onCanvas.300">
                            <Icon as={FiCalendar} boxSize={16} mb={4} />
                            <Text fontSize="xl">目前沒有聯絡簿紀錄</Text>
                        </Flex>
                    ) : (
                        <>
                            {visibleBooks.map(book => {
                                const isExpanded = expandedBookId === book.id;
                                const signed = isSigned(book);
                                // Try to find class name from book or match with child's classes
                                const className = book.class_name || (children[selectedChildIndex]?.classes?.find(c => c.id === book.class_id)?.name) || '班級';

                                return (
                                    <Card
                                        key={book.id}
                                        bg={signed ? "white" : "orange.50"}
                                        shadow="sm"
                                        borderRadius="2xl"
                                        border="1px solid"
                                        borderColor={signed ? "gray.100" : "orange.200"}
                                        borderLeft={signed ? "1px solid" : "6px solid"}
                                        borderLeftColor={signed ? "gray.100" : "orange.400"}
                                        overflow="hidden"
                                        onClick={() => toggleExpand(book.id)}
                                        cursor="pointer"
                                        transition="all 0.2s"
                                        _active={{ transform: 'scale(0.99)' }}
                                    >
                                        <CardBody p={5}>
                                            {/* Header */}
                                            <VStack align="stretch" spacing={2} mb={4}>
                                                <Flex justify="space-between" align="center">
                                                    {/* Left: Date & Weekday */}
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="2xl" fontWeight="bold" color="brand.800">
                                                            {new Date(book.date).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })}
                                                        </Text>
                                                        <Text fontSize="sm" color="gray.500">週{['日', '一', '二', '三', '四', '五', '六'][new Date(book.date).getDay()]}</Text>
                                                    </VStack>

                                                    {/* Right: Status Badge */}
                                                    <Badge
                                                        colorScheme={signed ? "green" : "red"}
                                                        fontSize="md" px={3} py={1} borderRadius="lg"
                                                        variant="solid"
                                                    >
                                                        {signed ? "已簽名" : "待簽名"}
                                                    </Badge>
                                                </Flex>

                                                {/* Class Badge (Moved to separate row to prevent overlap) */}
                                                <Box alignSelf="flex-start">
                                                    <Badge colorScheme={getClassColor(className)} variant="subtle" fontSize="md" px={2} py={1} borderRadius="md" whiteSpace="normal" textAlign="left">
                                                        {className}
                                                    </Badge>
                                                </Box>
                                            </VStack>

                                            {/* Content Area - Always Visible */}
                                            <VStack align="stretch" spacing={5} mb={3}>
                                                <Divider borderColor="gray.100" />

                                                {/* Contact Items */}
                                                <VStack align="stretch" spacing={4}>
                                                    {book.content.map((block, idx) => (
                                                        <Box key={idx}>
                                                            <Badge mb={2} colorScheme="gray" fontSize="md">{getDisplayLabel(block.label)}</Badge>
                                                            {block.type === 'photo' ? (
                                                                <HStack spacing={3} overflowX="auto" py={1}>
                                                                    {(block.photo_urls || []).map((url, pIdx) => (
                                                                        <Image
                                                                            key={pIdx}
                                                                            src={resolveImageUrl(url)}
                                                                            h="120px" borderRadius="xl" flexShrink={0}
                                                                            onClick={(e) => { e.stopPropagation(); openLightbox(url); }}
                                                                        />
                                                                    ))}
                                                                </HStack>
                                                            ) : (
                                                                <Text fontSize="xl" lineHeight="1.8" color="gray.800" whiteSpace="pre-wrap">{block.text}</Text>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </VStack>

                                                {/* Teacher Feedback */}
                                                {book.feedbacks?.length > 0 && (
                                                    <Box bg="orange.50" p={4} borderRadius="xl">
                                                        <HStack mb={2} color="orange.600">
                                                            <Icon as={FiSmile} />
                                                            <Text fontWeight="bold">老師回饋</Text>
                                                        </HStack>
                                                        <Text fontSize="lg" color="gray.700">{book.feedbacks[0].comment}</Text>
                                                    </Box>
                                                )}
                                            </VStack>

                                            {/* Collapsible Area - Photos & Signature */}
                                            <Collapse in={isExpanded} animateOpacity>
                                                <VStack align="stretch" spacing={5}>
                                                    {/* Photos */}
                                                    {book.photos?.length > 0 && (
                                                        <HStack spacing={3} overflowX="auto" py={2}>
                                                            {book.photos.map((url, idx) => (
                                                                <Image
                                                                    key={idx}
                                                                    src={resolveImageUrl(url)}
                                                                    h="120px"
                                                                    borderRadius="xl"
                                                                    onClick={(e) => { e.stopPropagation(); openLightbox(url); }}
                                                                />
                                                            ))}
                                                        </HStack>
                                                    )}

                                                    {/* Signature Section */}
                                                    <Box pt={4} borderTop={book.photos?.length > 0 ? "1px solid" : "none"} borderColor="gray.100">
                                                        {signed ? (
                                                            <VStack align="stretch" spacing={3} bg="green.50" p={4} borderRadius="xl">
                                                                <Flex align="center" color="green.700">
                                                                    <Icon as={FiCheckCircle} mr={2} boxSize={5} />
                                                                    <Text fontSize="lg" fontWeight="bold">家長已完成查閱與簽名</Text>
                                                                </Flex>

                                                                {/* Display Parent Comment */}
                                                                {(() => {
                                                                    const sig = book.signatures?.find(s => s.student_id === children[selectedChildIndex].id);
                                                                    return sig?.comment ? (
                                                                        <Box pl={7}>
                                                                            <Text fontSize="sm" color="gray.500" mb={1}>給老師的留言：</Text>
                                                                            <Text fontSize="md" color="gray.800" bg="white" p={3} borderRadius="md" shadow="sm">
                                                                                {sig.comment}
                                                                            </Text>
                                                                        </Box>
                                                                    ) : null;
                                                                })()}

                                                                {/* Teacher Reply Section */}
                                                                {(() => {
                                                                    const sig = book.signatures?.find(s => s.student_id === children[selectedChildIndex].id);

                                                                    if (sig?.teacher_reply) {
                                                                        return (
                                                                            <Box bg="blue.50" p={3} borderRadius="md" mt={2} borderLeft="4px solid" borderColor="blue.400">
                                                                                <Text fontSize="sm" fontWeight="bold" color="blue.600" mb={1}>老師回覆：</Text>
                                                                                <Text fontSize="md" color="gray.800">{sig.teacher_reply}</Text>
                                                                            </Box>
                                                                        );
                                                                    } else if (sig?.teacher_read_at) {
                                                                        return (
                                                                            <Flex align="center" color="blue.600" mt={2} pl={2}>
                                                                                <Icon as={FiCheckCircle} mr={2} boxSize={5} />
                                                                                <Text fontSize="md" fontWeight="bold">老師已讀</Text>
                                                                            </Flex>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                            </VStack>
                                                        ) : (
                                                            <VStack align="stretch" spacing={3} onClick={e => e.stopPropagation()}>
                                                                <Textarea
                                                                    placeholder="給老師的留言..."
                                                                    value={signComment}
                                                                    onChange={e => setSignComment(e.target.value)}
                                                                    bg="gray.50"
                                                                    fontSize="lg"
                                                                />
                                                                <Button
                                                                    w="100%"
                                                                    colorScheme="brand"
                                                                    size="lg"
                                                                    h="60px"
                                                                    fontSize="xl"
                                                                    onClick={(e) => handleSign(book.id, e)}
                                                                    isLoading={isSigning}
                                                                >
                                                                    簽名確認
                                                                </Button>
                                                            </VStack>
                                                        )}
                                                    </Box>
                                                </VStack>
                                            </Collapse>

                                            {!isExpanded && (
                                                <Flex justify="center" mt={2}>
                                                    <Text fontSize="sm" color="gray.400">
                                                        {signed ? "點擊查看詳細內容" : "點擊進行簽名"}
                                                    </Text>
                                                </Flex>
                                            )}
                                        </CardBody>
                                    </Card>
                                );
                            })}

                            {hasMore && (
                                <Button
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() => setVisibleCount(prev => prev + 7)}
                                    size="lg"
                                    w="100%"
                                    py={6}
                                >
                                    載入更多紀錄
                                </Button>
                            )}
                        </>
                    )}

                    {/* Lightbox Modal */}
                    <Modal isOpen={!!selectedImage} onClose={closeLightbox} size="full" isCentered>
                        <ModalOverlay bg="blackAlpha.900" />
                        <ModalContent bg="transparent" boxShadow="none" m={0}>
                            <ModalCloseButton
                                color="white"
                                bg="blackAlpha.600"
                                _hover={{ bg: "blackAlpha.800" }}
                                borderRadius="full"
                                zIndex={200}
                                size="lg"
                                position="fixed"
                                top="calc(env(safe-area-inset-top) + 12px)"
                                right="16px"
                            />
                            <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                                {selectedImage && (
                                    <TransformWrapper doubleClick={{ mode: "reset" }}>
                                        <TransformComponent
                                            wrapperStyle={{ width: "100vw", height: "100vh" }}
                                            contentStyle={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        >
                                            {/* 對齊全螢幕寬：w=100vw + objectFit contain（過高才縮以符合高度） */}
                                            <Image src={selectedImage} w="100vw" maxH="100vh" objectFit="contain" />
                                        </TransformComponent>
                                    </TransformWrapper>
                                )}
                            </ModalBody>
                        </ModalContent>
                    </Modal>

                </VStack>
            )}
        </>
    );
};

export default ParentCommBookPage;
